import React, { useState } from "react";

function splitParagraphs(value = "") {
  return String(value || "")
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function splitLines(value = "") {
  return String(value || "")
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function getIssueNumber(issueLine = "") {
  const match = String(issueLine || "").match(/\d+/);
  return match?.[0]?.padStart(4, "0") || "0001";
}

function formatIssueDate(value) {
  const source = value ? new Date(value) : new Date();

  if (Number.isNaN(source.getTime())) {
    return new Date().toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  return source.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function hasText(value) {
  return String(value || "").trim().length > 0;
}

function hasAnyText(...values) {
  return values.some((value) => hasText(value));
}

function EditorialParagraphs({ value, variant = "body" }) {
  const paragraphs = splitParagraphs(value);
  if (!paragraphs.length) return null;

  const style =
    variant === "lead"
      ? styles.leadText
      : variant === "cta"
        ? styles.ctaText
        : styles.bodyText;

  return (
    <>
      {paragraphs.map((paragraph, index) => (
        <p key={index} className="zedroEditorialPreviewParagraph" style={style}>
          {paragraph}
        </p>
      ))}
    </>
  );
}


function EditorialContentTitle({ value }) {
  if (!hasText(value)) return null;

  return <h2 className="zedroEditorialPreviewContentTitle" style={styles.contentTitle}>{value}</h2>;
}

function EditorialListOrReflection({ value }) {
  const paragraphs = splitParagraphs(value);
  const lines = splitLines(value);

  if (!lines.length) return null;

  const shouldRenderAsList = lines.length >= 2 && paragraphs.length <= 1;

  if (!shouldRenderAsList) {
    return <EditorialParagraphs value={value} />;
  }

  return (
    <ul className="zedroEditorialPreviewList" style={styles.cleanList}>
      {lines.map((line, index) => (
        <li key={index}>{line}</li>
      ))}
    </ul>
  );
}

function EditorialImage({ src, caption, alt = "" }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) return null;

  return (
    <figure className="zedroEditorialPreviewFigure" style={styles.figure}>
      <img
        src={src}
        alt={alt}
        onError={() => setFailed(true)}
        className="zedroEditorialPreviewImage"
        style={styles.image}
      />

      {caption && <figcaption style={styles.caption}>{caption}</figcaption>}
    </figure>
  );
}

function EditorialSection({
  children,
  image,
  imageCaption,
  imageAlt,
  variant = "default",
}) {
  const hasImage = Boolean(image);

  return (
    <section
      className={`zedroEditorialPreviewSection ${variant === "soft" ? "soft" : ""} ${variant === "cta" ? "cta" : ""}`}
      style={{
        ...styles.section,
        ...(variant === "soft" ? styles.sectionSoft : {}),
        ...(variant === "cta" ? styles.sectionCta : {}),
      }}
    >
      <div className={hasImage ? "zedroEditorialPreviewSectionGrid" : "zedroEditorialPreviewSectionFull"} style={hasImage ? styles.sectionGrid : styles.sectionFull}>
        <div>{children}</div>

        {hasImage && (
          <EditorialImage
            src={image}
            caption={imageCaption}
            alt={imageAlt}
          />
        )}
      </div>
    </section>
  );
}


const PREVIEW_RESPONSIVE_STYLES = `
  @media (max-width: 760px) {
    .zedroEditorialPreviewPage {
      padding: 0 !important;
      background: #ffffff !important;
      min-height: auto !important;
    }

    .zedroEditorialPreviewContainer {
      max-width: 100% !important;
      width: 100% !important;
      margin: 0 !important;
    }

    .zedroEditorialPreviewWrapper {
      border-radius: 0 !important;
      box-shadow: none !important;
      border: 0 !important;
    }

    .zedroEditorialPreviewBrandInner {
      padding: 22px 18px 26px !important;
    }

    .zedroEditorialPreviewBrandTopline {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 8px !important;
      text-align: center !important;
      margin-bottom: 22px !important;
    }

    .zedroEditorialPreviewLogo {
      font-size: 27px !important;
      letter-spacing: 0.12em !important;
      white-space: nowrap !important;
    }

    .zedroEditorialPreviewHero {
      padding: 30px 20px 24px !important;
    }

    .zedroEditorialPreviewHeroTitle {
      max-width: 100% !important;
      font-size: clamp(28px, 8.5vw, 38px) !important;
      line-height: 1.1 !important;
    }

    .zedroEditorialPreviewHeroSub {
      max-width: 100% !important;
      font-size: 15.5px !important;
      line-height: 1.6 !important;
    }

    .zedroEditorialPreviewSection {
      padding: 0 20px 30px !important;
    }

    .zedroEditorialPreviewSection.soft {
      padding-top: 24px !important;
      padding-bottom: 24px !important;
    }

    .zedroEditorialPreviewSection.cta {
      margin: 0 20px 30px !important;
      padding: 24px 20px 26px !important;
      border-radius: 22px !important;
    }

    .zedroEditorialPreviewSectionGrid {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 18px !important;
    }

    .zedroEditorialPreviewWrapper p {
      font-size: 16px !important;
      line-height: 1.76 !important;
    }

    .zedroEditorialPreviewContentTitle {
      font-size: 25px !important;
      line-height: 1.2 !important;
    }

    .zedroEditorialPreviewFigure {
      margin-top: 16px !important;
    }

    .zedroEditorialPreviewImage {
      border-radius: 18px !important;
      max-height: 360px !important;
      object-fit: cover !important;
    }

    .zedroEditorialPreviewQuote {
      margin: 22px 0 !important;
      padding: 17px 18px !important;
      font-size: 20px !important;
      line-height: 1.42 !important;
    }

    .zedroEditorialPreviewList {
      padding: 16px 18px 16px 34px !important;
      font-size: 15.5px !important;
    }

    .zedroEditorialPreviewFooter {
      padding: 24px 20px 30px !important;
    }
  }
`;

export default function EditorialPreview({ data = {} }) {
  const issueNumber = getIssueNumber(data.issue_line || data.edition);

  const issueDate = formatIssueDate(
    data.published_at || data.created_at || data.date || data.updated_at
  );

  const hasIdeaCentral = hasAnyText(data.idea_central, data.idea_image);

  const hasMainDevelopment = hasAnyText(
    data.bloque1_title,
    data.bloque1_p1,
    data.quote_text,
    data.bloque1_p2,
    data.inline_image
  );

  const hasAdditionalBlock = hasAnyText(data.callout_title, data.callout_text);
  const hasSecondAdditionalBlock = hasAnyText(data.step1, data.step2);
  const hasListOrReflection = hasText(data.step3);
  const hasNotes = hasText(data.callout_body);
  const hasClosing = hasAnyText(data.cierre_title, data.cierre_text);
  const hasCta = hasAnyText(data.practica, data.step4);

  return (
    <div className="zedroEditorialPreviewPage" style={styles.page}>
      <style>{PREVIEW_RESPONSIVE_STYLES}</style>
      <div className="zedroEditorialPreviewContainer" style={styles.container}>
        <article className="zedroEditorialPreviewWrapper" style={styles.wrapper}>
          <header className="zedroEditorialPreviewBrandHeader" style={styles.brandHeader}>
            <div className="zedroEditorialPreviewBrandInner" style={styles.brandInner}>
              <div className="zedroEditorialPreviewBrandTopline" style={styles.brandTopline}>
                <span>EDICIÓN {issueNumber}</span>
                <span>{issueDate}</span>
              </div>

              <div style={styles.brandCenter}>
                <div className="zedroEditorialPreviewLogo" style={styles.logoWordmark}>Z&nbsp;&nbsp;E&nbsp;&nbsp;D&nbsp;&nbsp;R&nbsp;&nbsp;O</div>
                <div style={styles.logoTagline}>Formación con raíz eterna</div>
                <div style={styles.newsLabel}>ZEDRO News</div>
              </div>
            </div>
          </header>

          <section className="zedroEditorialPreviewHero" style={styles.hero}>
            <h1 className="zedroEditorialPreviewHeroTitle" style={styles.heroTitle}>
              {data.hero_title || data.title || "Título principal de la edición"}
            </h1>

            {(data.hero_sub || data.summary) && (
              <p className="zedroEditorialPreviewHeroSub" style={styles.heroSub}>{data.hero_sub || data.summary}</p>
            )}

            <EditorialImage
              src={data.hero_image || data.cover_url}
              alt={data.hero_title || data.title || "Imagen principal"}
            />
          </section>

          {data.lead && (
            <EditorialSection variant="soft">
              <EditorialParagraphs value={data.lead} variant="lead" />
            </EditorialSection>
          )}

          {hasIdeaCentral && (
            <EditorialSection
              image={data.idea_image}
              imageCaption={data.idea_image_caption}
              imageAlt={data.hero_title || data.title || "Imagen editorial"}
            >
              <EditorialParagraphs value={data.idea_central} />
            </EditorialSection>
          )}

          {hasMainDevelopment && (
            <EditorialSection
              image={data.inline_image}
              imageCaption={data.inline_caption}
              imageAlt={data.hero_title || data.title || "Imagen editorial"}
            >
              <EditorialContentTitle value={data.bloque1_title} />
              <EditorialParagraphs value={data.bloque1_p1} />

              {data.quote_text && (
                <blockquote className="zedroEditorialPreviewQuote" style={styles.quoteBox}>
                  “{data.quote_text}”
                </blockquote>
              )}

              <EditorialParagraphs value={data.bloque1_p2} />
            </EditorialSection>
          )}

          {hasAdditionalBlock && (
            <EditorialSection variant="soft">
              <EditorialContentTitle value={data.callout_title} />
              <EditorialParagraphs value={data.callout_text} />
            </EditorialSection>
          )}

          {hasSecondAdditionalBlock && (
            <EditorialSection>
              <EditorialContentTitle value={data.step1} />
              <EditorialParagraphs value={data.step2} />
            </EditorialSection>
          )}

          {hasListOrReflection && (
            <EditorialSection variant="soft">
              <EditorialListOrReflection value={data.step3} />
            </EditorialSection>
          )}

          {hasNotes && (
            <EditorialSection>
              <div style={styles.noteBox}>
                <EditorialParagraphs value={data.callout_body} />
              </div>
            </EditorialSection>
          )}

          {hasClosing && (
            <EditorialSection>
              <EditorialContentTitle value={data.cierre_title} />
              <EditorialParagraphs value={data.cierre_text} />
            </EditorialSection>
          )}

          {hasCta && (
            <EditorialSection variant="cta">
              <EditorialParagraphs value={data.practica} variant="cta" />

              {data.step4 && (
                <div style={styles.ctaActions}>
                  <a href="/store" style={styles.ctaButton}>
                    {data.step4}
                  </a>
                </div>
              )}
            </EditorialSection>
          )}

          <footer className="zedroEditorialPreviewFooter" style={styles.footer}>
            <p style={styles.footerBrand}>ZEDRO Academy</p>
            <p>© 2026 Académie ZEDRO — ZEDRO Academy. Todos los derechos reservados.</p>
            <p>
              Recibes este contenido porque haces parte de la comunidad de
              ZEDRO Academy.
            </p>
          </footer>
        </article>
      </div>
    </div>
  );
}

const styles = {
  page: {
    background:
      "linear-gradient(180deg, rgba(15,47,70,0.08) 0%, #f5f7fa 42%, #ffffff 100%)",
    padding: "40px 16px",
    minHeight: "100%",
  },

  container: {
    maxWidth: "760px",
    margin: "0 auto",
  },

  wrapper: {
    overflow: "hidden",
    borderRadius: 24,
    background: "#ffffff",
    color: "#203040",
    boxShadow: "0 30px 80px rgba(15,47,70,0.14)",
    fontFamily: "var(--font-body, Questrial, sans-serif)",
    lineHeight: 1.7,
  },

  brandHeader: {
    background:
      "linear-gradient(135deg, #0f2f46 0%, #173f5b 58%, #0c2638 100%)",
    padding: "0",
    color: "#ffffff",
    borderBottom: "4px solid rgba(207,174,85,.92)",
  },

  brandInner: {
    padding: "26px 34px 34px",
  },

  brandTopline: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    fontSize: 10.5,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.78)",
    marginBottom: 30,
    padding: "0 4px",
  },

  brandCenter: {
    textAlign: "center",
  },

  logoWordmark: {
    fontFamily: "var(--font-title, 'Playfair Display', serif)",
    fontSize: 34,
    letterSpacing: "0.16em",
    color: "#d7a951",
    lineHeight: 1,
    marginBottom: 7,
  },

  logoTagline: {
    fontSize: 12,
    color: "rgba(255,255,255,0.92)",
    marginBottom: 22,
  },

  newsLabel: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 34,
    padding: "0 18px",
    borderRadius: 999,
    border: "1px solid rgba(207,174,85,.34)",
    background: "rgba(255,255,255,.055)",
    color: "#f4d27a",
    fontSize: 16,
    fontWeight: 900,
    letterSpacing: "0.02em",
  },

  hero: {
    padding: "46px 46px 34px",
    textAlign: "center",
  },

  heroTitle: {
    margin: "0 auto 16px",
    maxWidth: 660,
    fontFamily: "var(--font-title, 'Playfair Display', serif)",
    fontSize: 40,
    lineHeight: 1.12,
    color: "#0f2f46",
  },

  heroSub: {
    margin: "0 auto 26px",
    maxWidth: 590,
    fontSize: 16,
    lineHeight: 1.65,
    color: "rgba(15,47,70,0.68)",
  },

  section: {
    padding: "0 46px 38px",
  },

  sectionSoft: {
    paddingTop: 28,
    paddingBottom: 30,
    background:
      "linear-gradient(180deg, rgba(15,47,70,0.035) 0%, rgba(255,255,255,0) 100%)",
    borderTop: "1px solid rgba(15,47,70,0.06)",
    borderBottom: "1px solid rgba(15,47,70,0.05)",
  },

  sectionCta: {
    margin: "0 46px 40px",
    padding: "30px 30px 32px",
    borderRadius: 24,
    background:
      "linear-gradient(135deg, rgba(15,47,70,0.96) 0%, rgba(23,63,91,0.96) 100%)",
    color: "#ffffff",
    border: "1px solid rgba(207,174,85,.26)",
    boxShadow: "0 22px 46px rgba(15,47,70,0.18)",
  },

  sectionFull: {
    display: "block",
  },

  sectionGrid: {
    display: "grid",
    gridTemplateColumns: "1.18fr 0.82fr",
    gap: 24,
    alignItems: "start",
  },

  contentTitle: {
    margin: "0 0 16px",
    fontFamily: "var(--font-title, 'Playfair Display', serif)",
    fontSize: 26,
    lineHeight: 1.25,
    fontWeight: 500,
    color: "#0f2f46",
  },

  leadText: {
    margin: "0 0 16px",
    fontSize: 17,
    lineHeight: 1.8,
    color: "rgba(15,47,70,0.82)",
  },

  bodyText: {
    margin: "0 0 16px",
    fontSize: 15.5,
    lineHeight: 1.82,
    color: "rgba(32,48,64,0.86)",
  },

  figure: {
    margin: "20px 0 0",
  },

  image: {
    width: "100%",
    display: "block",
    borderRadius: 20,
    boxShadow: "0 18px 36px rgba(15,47,70,0.12)",
    border: "1px solid rgba(15,47,70,.08)",
  },

  caption: {
    marginTop: 8,
    fontSize: 12,
    lineHeight: 1.45,
    color: "rgba(15,47,70,0.54)",
    textAlign: "center",
  },

  quoteBox: {
    margin: "24px auto",
    padding: "18px 22px",
    borderLeft: "3px solid #d6ad4f",
    background: "rgba(204,174,73,0.08)",
    color: "#0f2f46",
    fontFamily: "var(--font-title, 'Playfair Display', serif)",
    fontSize: 19,
    lineHeight: 1.45,
    borderRadius: "0 18px 18px 0",
  },

  cleanList: {
    margin: "18px 0 0",
    padding: "18px 20px 18px 38px",
    borderRadius: 18,
    background: "#ffffff",
    border: "1px solid rgba(15,47,70,0.08)",
    color: "rgba(32,48,64,0.88)",
    fontSize: 15.5,
    lineHeight: 1.75,
  },

  noteBox: {
    padding: "22px 24px 8px",
    borderRadius: 22,
    background: "rgba(15,47,70,0.045)",
    border: "1px solid rgba(15,47,70,0.08)",
  },

  ctaText: {
    margin: "0 0 16px",
    fontSize: 15.5,
    lineHeight: 1.82,
    color: "rgba(255,255,255,0.86)",
  },

  ctaActions: {
    display: "flex",
    justifyContent: "center",
    marginTop: 22,
  },

  ctaButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 44,
    padding: "0 22px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #d7a951 0%, #f0cf75 100%)",
    color: "#0f2f46",
    textDecoration: "none",
    fontSize: 14,
    fontWeight: 900,
    boxShadow: "0 14px 30px rgba(0,0,0,.16)",
  },

  footer: {
    padding: "26px 42px 32px",
    borderTop: "1px solid rgba(15,47,70,0.08)",
    background: "rgba(15,47,70,0.035)",
    textAlign: "center",
    fontSize: 12,
    lineHeight: 1.65,
    color: "rgba(15,47,70,0.58)",
  },

  footerBrand: {
    margin: "0 0 8px",
    fontFamily: "var(--font-title, 'Playfair Display', serif)",
    fontSize: 18,
    color: "#0f2f46",
  },
};
